import React, { useEffect, useState } from 'react';
import {
  Box, Stack, List, ListItem, ListItemText, IconButton, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WorkIcon from '@mui/icons-material/Work';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';
import { useTranslation } from '../i18n/i18n';

export default function PositionsSettings() {
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [editIndex, setEditIndex] = useState(-1);
  const [editValue, setEditValue] = useState('');
  const [confirmIndex, setConfirmIndex] = useState(-1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    setLoading(true);
    fetch(`${API_ENDPOINTS.BASEURL}/api/positions`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setPositions(Array.isArray(data) ? data : []))
      .catch(() => setPositions([]))
      .finally(() => setLoading(false));
  }, []);

  const savePositions = (updated) => {
    setLoading(true);
    fetch(`${API_ENDPOINTS.BASEURL}/api/positions`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ positions: updated })
    })
      .then(async res => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(data => {
        setPositions(data.positions || updated);
        setSnackbar({ open: true, message: t('position_saved'), severity: 'success' });
      })
      .catch(() => setSnackbar({ open: true, message: t('position_save_failed'), severity: 'error' }))
      .finally(() => setLoading(false));
  };

  const handleAdd = () => {
    const name = (newName || '').trim();
    if (!name) return setSnackbar({ open: true, message: t('name_required'), severity: 'error' });
    if (positions.some(p => p.display_text.toLowerCase() === name.toLowerCase())) return setSnackbar({ open: true, message: t('name_duplicated'), severity: 'error' });
    const updated = [...positions, { display_text: name }];
    savePositions(updated);
    setNewName('');
  };

  const handleEditSave = () => {
    const name = (editValue || '').trim();
    if (!name) return setSnackbar({ open: true, message: t('name_required'), severity: 'error' });
    const duplicate = positions.find((p, i) => p.display_text.toLowerCase() === name.toLowerCase() && i !== editIndex);
    if (duplicate) return setSnackbar({ open: true, message: t('name_duplicated'), severity: 'error' });
    const updated = positions.slice();
    updated[editIndex] = { ...updated[editIndex], display_text: name };
    savePositions(updated);
    setEditIndex(-1);
    setEditValue('');
  };

  const handleDeleteConfirmed = () => {
    const updated = positions.filter((_, i) => i !== confirmIndex);
    savePositions(updated);
    setConfirmIndex(-1);
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      {/* Add Position */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder={t('new_position')}
          size="small"
          sx={{ flex: 1 }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}
        >
          {t('add')}
        </Button>
      </Box>

      {/* Positions List */}
      {positions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Avatar sx={{ width: 48, height: 48, mb: 1, bgcolor: 'grey.100', color: 'grey.400', mx: 'auto' }}>
            <WorkIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            {t('no_positions') || 'No positions configured yet.'}
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {positions.map((p, i) => (
            <ListItem
              key={p.id}
              sx={{
                px: 1,
                py: 1,
                borderRadius: 1,
                mb: 0.5,
                '&:hover': { bgcolor: 'action.hover' }
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => { setEditIndex(i); setEditValue(p.display_text); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setConfirmIndex(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' + '14', color: 'primary.main', mr: 1.5, fontSize: '0.8rem' }}>
                <WorkIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <ListItemText
                primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{p.display_text}</Typography>}
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* Edit Dialog */}
      <Dialog open={editIndex !== -1} onClose={() => setEditIndex(-1)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('position_edit_title')}
          </Typography>
          <IconButton onClick={() => setEditIndex(-1)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); }}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setEditIndex(-1)} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleEditSave} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmIndex !== -1} onClose={() => setConfirmIndex(-1)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('position_delete_title')}
          </Typography>
          <IconButton onClick={() => setConfirmIndex(-1)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('position_delete_prompt', { name: positions[confirmIndex]?.display_text || '' })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setConfirmIndex(-1)} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDeleteConfirmed} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} severity={snackbar.severity} />
    </Box>
  );
}
